/**
 * Helpers for code that mixes screen pixels with layout pixels while the UI
 * is scaled with CSS zoom on small screens (index.css --ui-zoom).
 *
 * Under CSS zoom, getBoundingClientRect / clientX / clientY are in screen
 * pixels while offsetWidth, scrollTop, style lengths and Mapbox's e.point
 * (which it scales by offsetWidth / rect.width) are in layout pixels. Any
 * value that crosses between the two must be multiplied or divided by the
 * zoom factor.
 */

/** The UI zoom factor in effect (1 when the UI is not scaled). */
export function getUiZoom(): number {
  if (typeof document === "undefined") return 1;
  const raw = getComputedStyle(document.documentElement).getPropertyValue("--ui-zoom");
  const z = parseFloat(raw);
  return Number.isFinite(z) && z > 0 ? z : 1;
}

/** Convert a screen-pixel length (from a client rect / clientX) to layout px. */
export function screenToLayout(px: number): number {
  return px / getUiZoom();
}

/** Convert a layout-pixel length (offsetWidth, style px) to screen px. */
export function layoutToScreen(px: number): number {
  return px * getUiZoom();
}

/**
 * Make a Mapbox GL container report its LAYOUT size from getBoundingClientRect.
 * Mapbox 3.9 sizes its canvas from the container's screen rect, which under
 * CSS zoom is the zoomed size, so the map would only fill --ui-zoom of its
 * box. Left/top stay in screen px. Pointer maths is unaffected: Mapbox scales
 * mouse positions by offsetWidth / rect.width of the CANVAS container (a
 * child), so e.point stays in layout px (see mapEventScreenPoint). Call once,
 * before constructing the map.
 */
export function reportLayoutSizeToMapbox(el: HTMLElement): void {
  const nativeRect = el.getBoundingClientRect.bind(el);
  el.getBoundingClientRect = () => {
    const r = nativeRect();
    return new DOMRect(r.left, r.top, el.offsetWidth, el.offsetHeight);
  };
}

type MapPointerEvent = {
  point: { x: number; y: number };
  originalEvent?: MouseEvent | TouchEvent | PointerEvent | Event;
  target?: { getContainer(): HTMLElement };
};

/**
 * The viewport position of a Mapbox pointer event, for MUI's anchorPosition.
 * Uses the browser event's clientX/clientY (already screen px); falls back to
 * e.point (layout px relative to the map canvas) converted and offset by the
 * map container's screen rect.
 */
export function mapEventScreenPoint(e: MapPointerEvent): { left: number; top: number } {
  const oe = e.originalEvent as { clientX?: number; clientY?: number; touches?: TouchList } | undefined;
  const touch = oe?.touches?.[0];
  const clientX = touch?.clientX ?? oe?.clientX;
  const clientY = touch?.clientY ?? oe?.clientY;
  if (typeof clientX === "number" && typeof clientY === "number") {
    return { left: clientX, top: clientY };
  }
  const rect = e.target?.getContainer().getBoundingClientRect();
  return {
    left: layoutToScreen(e.point.x) + (rect?.left ?? 0),
    top: layoutToScreen(e.point.y) + (rect?.top ?? 0),
  };
}
